// signals-web.ts — 1:1 JS port of src-tauri/src/signals.rs: the StashSignals engine.
// 🔴 WHY: compute_signals was a zero shell on web (fleet zero-stub sweep 2026-08-20) — the
// stash recap had nothing to narrate. Where this and the Rust could drift, the Rust wins.
// Hardened per the Oracle lesson (?? guards). Includes the db.rs norm_weight port (patterns
// store raw weight text; the fit math needs the canonical class).
//
// THE FENCE TRAVELS: deterministic stash aggregates — usable yardage (partial-skein aware),
// pattern fit, dye-lot risk. Never which-yarn-to-buy advice, never ranking, never valuation.
// Raw notes / shop names never enter (only the user's own aggregate spend total).

import type { Skein, Pattern, Project } from "./store";

const s = (v: unknown) => String(v ?? "");
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const round1 = (x: number) => Math.round(x * 10) / 10;
const round2 = (x: number) => Math.round(x * 100) / 100;
const parseDay = (v: unknown) => (/^\d{4}-\d{2}-\d{2}/.test(s(v)) ? s(v).slice(0, 10) : null);
const addDaysIso = (d: string, k: number) => new Date(Date.parse(`${d}T00:00:00Z`) + k * 86400000).toISOString().slice(0, 10);

/** Port of db.rs norm_weight — canonical yarn-weight classes. */
export function normWeight(raw: string): string {
  const t = raw.trim().toLowerCase();
  switch (t) {
    case "0": case "lace": case "lace weight": case "cobweb": case "thread": return "lace";
    case "1": case "fingering": case "sock": case "super fine": case "4ply": case "4 ply": return "fingering";
    case "2": case "sport": case "fine": case "5ply": return "sport";
    case "3": case "dk": case "light": case "8ply": case "8 ply": return "dk";
    case "4": case "worsted": case "medium": case "afghan": case "aran/worsted": case "10ply": return "worsted";
    case "5": case "aran": case "12ply": return "aran";
    case "6": case "bulky": case "chunky": case "craft": case "rug": return "bulky";
    case "7": case "super bulky": case "super-bulky": case "superbulky": case "roving": return "super-bulky";
    case "8": case "jumbo": return "jumbo";
    default: return t;
  }
}

/** Usable yardage for one stash line, partial-skein aware (mirrors Rust). */
function usableYardage(sk: Skein): number {
  const labelTotal = n(sk.yardage_per_skein) * Math.max(n(sk.skein_count), 0);
  if (n(sk.grams_per_skein) > 0 && n(sk.grams_remaining) >= 0) {
    const yardsPerGram = n(sk.yardage_per_skein) / n(sk.grams_per_skein);
    const byGrams = yardsPerGram * n(sk.grams_remaining);
    return Math.max(Math.min(byGrams, Math.max(labelTotal, 0)), 0);
  }
  return Math.max(labelTotal, 0);
}

function median(xs: number[]): number | null {
  const v = xs.filter((x) => Number.isFinite(x) && x > 0).sort((a, b) => a - b);
  if (!v.length) return null;
  const m = v.length;
  return m % 2 === 1 ? v[Math.floor(m / 2)] : (v[m / 2 - 1] + v[m / 2]) / 2;
}

export function computeSignalsWeb(skeins: Skein[], patterns: Pattern[], projects: Project[], today: string) {
  // ── Per-weight aggregation ──
  const wmap = new Map<string, { lines: number; phys: number; usable: number; ys: number[] }>();
  let totalUsable = 0, totalLabel = 0, physical = 0, partialLines = 0;
  for (const sk of skeins) {
    const u = usableYardage(sk);
    const label = n(sk.yardage_per_skein) * Math.max(n(sk.skein_count), 0);
    totalUsable += u;
    totalLabel += label;
    physical += Math.max(n(sk.skein_count), 0);
    if (n(sk.grams_per_skein) > 0 && n(sk.grams_remaining) > 0) {
      const full = n(sk.grams_per_skein) * Math.max(n(sk.skein_count), 0);
      if (n(sk.grams_remaining) + 0.5 < full) partialLines++;
    }
    const w = s(sk.weight) === "" ? "unspecified" : s(sk.weight);
    const e = wmap.get(w) ?? { lines: 0, phys: 0, usable: 0, ys: [] };
    e.lines += 1;
    e.phys += Math.max(n(sk.skein_count), 0);
    e.usable += u;
    if (n(sk.yardage_per_skein) > 0) e.ys.push(n(sk.yardage_per_skein));
    wmap.set(w, e);
  }
  const typicalFor = (w: string): number | null => {
    const e = wmap.get(w);
    if (!e) return null;
    const m = median(e.ys);
    return m != null ? round1(m) : null;
  };
  const by_weight = [...wmap.entries()]
    .map(([weight, e]) => ({
      weight,
      skein_lines: e.lines,
      physical_skeins: round1(e.phys),
      usable_yardage: round1(e.usable),
      typical_skein_yardage: (() => { const m = median(e.ys); return m != null ? round1(m) : null; })(),
    }))
    .sort((a, b) => b.usable_yardage - a.usable_yardage);

  // ── Fiber breakdown ──
  const fmap = new Map<string, { lines: number; usable: number }>();
  for (const sk of skeins) {
    const f = s(sk.fiber).trim() === "" ? "unspecified" : s(sk.fiber).trim().toLowerCase();
    const e = fmap.get(f) ?? { lines: 0, usable: 0 };
    e.lines += 1;
    e.usable += usableYardage(sk);
    fmap.set(f, e);
  }
  const by_fiber = [...fmap.entries()]
    .map(([fiber, e]) => ({
      fiber,
      skein_lines: e.lines,
      usable_yardage: round1(e.usable),
      share_pct: totalUsable > 0 ? round1((e.usable / totalUsable) * 100) : 0,
    }))
    .sort((a, b) => b.usable_yardage - a.usable_yardage)
    .slice(0, 8);

  // ── Top colorways ──
  const cmap = new Map<string, { lines: number; usable: number }>();
  for (const sk of skeins) {
    const c = s(sk.colorway).trim();
    if (!c) continue;
    const e = cmap.get(c) ?? { lines: 0, usable: 0 };
    e.lines += 1;
    e.usable += usableYardage(sk);
    cmap.set(c, e);
  }
  const colorways_top = [...cmap.entries()]
    .map(([colorway, e]) => ({ colorway, skein_lines: e.lines, usable_yardage: round1(e.usable) }))
    .sort((a, b) => b.usable_yardage - a.usable_yardage)
    .slice(0, 6);

  // ── Spend total ──
  const spendByCcy = new Map<string, { amount: number; n: number }>();
  for (const sk of skeins) {
    if (n(sk.price) > 0) {
      const ccy = s(sk.currency).trim() === "" ? "—" : s(sk.currency).trim().toUpperCase();
      const e = spendByCcy.get(ccy) ?? { amount: 0, n: 0 };
      e.amount += n(sk.price);
      e.n += 1;
      spendByCcy.set(ccy, e);
    }
  }
  let total_spend: { amount: number; currency: string; single_currency: boolean; priced_lines: number } | null = null;
  if (spendByCcy.size === 1) {
    const [ccy, e] = [...spendByCcy.entries()][0];
    total_spend = { amount: round2(e.amount), currency: ccy, single_currency: true, priced_lines: e.n };
  } else if (spendByCcy.size > 1) {
    let best: [string, { amount: number; n: number }] | null = null;
    for (const kv of spendByCcy) if (best == null || kv[1].amount > best[1].amount) best = kv;
    total_spend = { amount: round2(best![1].amount), currency: best![0], single_currency: false, priced_lines: best![1].n };
  }

  // ── Pattern fit ──
  let queuedPatterns = 0, makingPatterns = 0;
  const pattern_fit: {
    pattern: string; status: string; yarn_weight: string; needs_yardage: number;
    stash_yardage_same_weight: number; delta: number; fit: string;
    skeins_short: number | null; typical_skein_yardage: number | null;
  }[] = [];
  for (const p of patterns) {
    const status = s((p as unknown as { status?: unknown }).status);
    if (status === "queued") queuedPatterns++;
    else if (status === "making") makingPatterns++;
    if (status !== "queued" && status !== "making") continue;
    const w = s(p.yarn_weight) === "" ? "unspecified" : normWeight(s(p.yarn_weight));
    const stash = wmap.get(w)?.usable ?? 0;
    const needs = Math.max(n(p.total_yardage), 0);
    let fit: string;
    if (needs <= 0 || s(p.yarn_weight) === "") fit = "unknown";
    else if (stash + 0.5 >= needs) fit = "covered";
    else fit = "short";
    const typical = typicalFor(w);
    const skeinsShort = fit === "short" && typical != null && typical > 0 ? Math.ceil((needs - stash) / typical) : null;
    pattern_fit.push({
      pattern: s(p.name),
      status,
      yarn_weight: w,
      needs_yardage: round1(needs),
      stash_yardage_same_weight: round1(stash),
      delta: round1(stash - needs),
      fit,
      skeins_short: skeinsShort,
      typical_skein_yardage: typical,
    });
  }
  const rank = (f: { fit: string }) => (f.fit === "short" ? 0 : f.fit === "unknown" ? 1 : 2);
  pattern_fit.sort((a, b) => rank(a) - rank(b) || a.delta - b.delta);
  pattern_fit.length = Math.min(pattern_fit.length, 12);

  // ── Dye-lot alerts ──
  const lotmap = new Map<string, { brand: string; colorway: string; lots: Set<string>; usable: number }>();
  for (const sk of skeins) {
    const brand = s(sk.brand).trim(), colorway = s(sk.colorway).trim();
    if (!brand || !colorway) continue;
    const key = `${brand.toLowerCase()}|${colorway.toLowerCase()}`;
    const e = lotmap.get(key) ?? { brand: brand.toLowerCase(), colorway: colorway.toLowerCase(), lots: new Set<string>(), usable: 0 };
    const lot = s(sk.dye_lot).trim();
    if (lot) e.lots.add(lot);
    e.usable += usableYardage(sk);
    lotmap.set(key, e);
  }
  const dye_lot_alerts = [...lotmap.values()]
    .filter((e) => e.lots.size > 1)
    .map((e) => ({
      brand: e.brand,
      colorway: e.colorway,
      distinct_lots: e.lots.size,
      lots: [...e.lots].sort(),
      usable_yardage: round1(e.usable),
    }))
    .sort((a, b) => b.distinct_lots - a.distinct_lots)
    .slice(0, 8);

  // ── Idle stash ──
  const oneYearAgo = addDaysIso(today, -365);
  let oldest = null as string | null;
  let linesOverYear = 0, usableOverYear = 0;
  for (const sk of skeins) {
    const d = parseDay(sk.bought_on);
    if (d == null) continue;
    oldest = oldest == null || d < oldest ? d : oldest;
    if (d < oneYearAgo) {
      linesOverYear++;
      usableOverYear += usableYardage(sk);
    }
  }
  const idle = {
    oldest_purchase: oldest,
    lines_over_one_year: linesOverYear,
    usable_yardage_over_one_year: round1(usableOverYear),
  };

  // ── Finished projects ──
  const finished = projects.filter((p) => s(p.finished_on).trim() !== "" || n(p.yardage_used) > 0);
  const totalUsed = finished.reduce((a, p) => a + Math.max(n(p.yardage_used), 0), 0);
  const finDates = projects.map((p) => parseDay(p.finished_on)).filter((d): d is string => d != null).sort();
  const projectsSummary = {
    finished_count: finished.length,
    total_yardage_used: round1(totalUsed),
    last_finished_on: finDates[finDates.length - 1] ?? null,
    avg_yardage_per_project: finished.length ? round1(totalUsed / finished.length) : null,
  };

  return {
    schema:
      "oriora.skein.v1-signals: deterministic stash aggregates (no raw rows). All yardage is in yards. usable_yardage is partial-skein-aware (scaled by grams remaining). pattern_fit.delta = stash_same_weight - needs (positive=surplus, negative=shortfall); skeins_short = ceil(shortfall / typical_skein_yardage). Every figure is a plain observation about the user's OWN logged stash — describe what they own and where each queued pattern stands; NEVER advise which yarn to buy, NEVER rank a yarn as better, NEVER value yarn for resale.",
    generated_for: today,
    totals: {
      skein_lines: skeins.length,
      physical_skeins: round1(physical),
      label_yardage: round1(totalLabel),
      usable_yardage: round1(totalUsable),
      partial_lines: partialLines,
      queued_patterns: queuedPatterns,
      making_patterns: makingPatterns,
      total_spend,
    },
    by_weight,
    by_fiber,
    colorways_top,
    pattern_fit,
    dye_lot_alerts,
    idle,
    projects: projectsSummary,
  };
}
